import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import styled from "styled-components";
import { getRecommendations } from "../services/mfuUserService";
import { getProfile } from "../services/profileService";
import MainHeader from "../Components/MainHeader";

const RecommendationsContainer = styled.div`
  min-height: 100vh;
  background-color: #f8f9fa;
  font-family: "Noto Sans Thai", sans-serif !important;
  * {
    font-family: "Noto Sans Thai", sans-serif !important;
    font-weight: normal;
    color: #666666;
  }
`;

const PageHeader = styled.div`
  background-color: #00bbd3;
  padding: 16px 24px;
  color: white;
  font-size: 18px;
  font-weight: bold;
  font-family: "Noto Sans Thai", sans-serif !important;
  max-width: 480px;
  margin: 0 auto;
  width: 100%;
  box-sizing: border-box;
`;

const Content = styled.div`
  padding: 24px;
  max-width: 480px;
  margin: 0 auto;
  width: 100%;
  box-sizing: border-box;
`;

const MainSection = styled.div`
  background: white;
  border-radius: 16px;
  padding: 24px;
  margin-bottom: 24px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
`;

const MainTitle = styled.h2`
  font-size: 16px;
  font-weight: bold;
  color: #00bbd3;
  margin: 0 0 4px 0;
  display: flex;
  align-items: center;
  gap: 8px;
  font-family: "Noto Sans Thai", sans-serif !important;
`;

const HealthIcon = styled.div`
  width: 24px;
  height: 24px;
  background: #00bbd3;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const MainContent = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 16px;
`;

const IllustrationContainer = styled.div`
  flex-shrink: 0;
  width: 140px;
  height: 120px;
  background: url("./Frame.png") center/contain no-repeat;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  align-self: center;
`;

const MainText = styled.div`
  flex: 1;
`;

const MainDescription = styled.div`
  font-size: 16px;
  color: #666666;
  line-height: 1.6;
  margin: 0 0 16px 0;
  font-family: "Noto Sans Thai", sans-serif !important;
`;

const ActionButtons = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const ActionButton = styled.div`
  background: transparent;
  border: 2px solid #00bbd3;
  color: #00bbd3;
  padding: 6px 16px;
  border-radius: 0px;
  font-size: 12px;
  font-weight: bold;
  font-family: "Noto Sans Thai", sans-serif !important;
`;

const SectionTitle = styled.h3`
  font-size: 18px;
  font-weight: bold;
  color: #00bbd3;
  margin: 0 0 20px 0;
  font-family: "Noto Sans Thai", sans-serif !important;
  display: flex;
  align-items: center;
  gap: 12px;
`;

const SectionIcon = styled.img`
  width: 40px;
  height: 40px;
  object-fit: contain;
  flex-shrink: 0;
`;

const RecommendationGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 8px;
  margin-bottom: 24px;
  justify-items: center;
`;

const RecommendationCard = styled.div`
  background: ${(props) => (props.active ? "#00BBD3" : "white")};
  border: 2px solid #00bbd3;
  border-radius: 0px;
  padding: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  transition: all 0.3s ease;
  cursor: pointer;
  width: 100px;
  height: 75px;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  }
`;

const CardIcon = styled.div`
  width: 64px;
  height: 64px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 4px;

  svg {
    width: 64px;
    height: 64px;
  }

  svg path {
    fill: ${(props) => (props.active ? "white" : "#00BBD3")};
  }
`;

const CardTitle = styled.h4`
  font-size: 12px;
  font-weight: bold;
  color: ${(props) => (props.active ? "white" : "#333")};
  margin: 0;
  font-family: "Noto Sans Thai", sans-serif !important;
`;

const DetailSection = styled.div`
  background: white;
  border-radius: 16px;
  padding: 20px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
`;

const DetailCard = styled.div`
  display: flex;
  flex-direction: row;
  gap: 16px;
  align-items: flex-start;
  padding: 4px 0;
`;

const DetailImage = styled.div`
  width: 85px;
  height: 85px;
  border-radius: 8px;
  background: url("${(props) => props.image}") center/cover no-repeat;
  flex-shrink: 0;
  align-self: flex-start;
`;

const DetailContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  min-width: 0;
  overflow-wrap: break-word;
`;

const DetailText = styled.div`
  font-size: 14px;
  color: #666666;
  line-height: 1.8;
  margin: 0;
  font-family: "Noto Sans Thai", sans-serif !important;
  white-space: pre-wrap;
  word-break: break-word;

  strong {
    font-weight: 700;
    color: #444;
  }

  p {
    margin: 0 0 6px 0;
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 50vh;
  font-size: 16px;
  color: #666;
`;

const ErrorMessage = styled.div`
  background: #ffebee;
  color: #c62828;
  padding: 16px;
  border-radius: 8px;
  margin: 16px 0;
  font-size: 14px;
  line-height: 1.5;
`;

const EmptyMessage = styled.div`
  text-align: center;
  padding: 40px 20px;
  color: #999;
  font-size: 16px;
`;

const renderAdviceText = (text) => {
  if (!text) return null;
  return text.split("\n").map((line, i) => {
    const parts = line.split(/\*\*(.+?)\*\*/g);
    return (
      <p key={i} style={{ margin: "0 0 4px 0" }}>
        {parts.map((part, j) =>
          j % 2 === 1 ? <strong key={j}>{part}</strong> : part,
        )}
      </p>
    );
  });
};

const RecommendationsPage = () => {
  const [activeTab, setActiveTab] = useState("food");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [recommendationsData, setRecommendationsData] = useState(null);
  const [profileData, setProfileData] = useState(null);

  const navigate = useNavigate();
  const location = useLocation();
  const visitDate = location.state?.visitDate || null;

  const handleProfileClick = () => {
    navigate("/profile-edit");
  };

  // Load data on mount
  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        setError(null);

        // Load profile and recommendations in parallel
        const [profileRes, recommendationsRes] = await Promise.all([
          getProfile().catch((err) => {
            return null;
          }),
          getRecommendations(null, visitDate).catch((err) => {
            return null;
          }),
        ]);

        if (profileRes) {
          let profile = profileRes.data || profileRes;

          // Convert image path to Firebase URL
          let imagePath = profile.image || profile.profile_image || "";
          let imageUrl = null;
          if (imagePath && imagePath !== "") {
            if (imagePath.startsWith("http")) {
              imageUrl = imagePath;
            } else {
              imageUrl = `https://firebasestorage.googleapis.com/v0/b/diet-z.appspot.com/o/${imagePath}`;
            }
          }

          setProfileData({
            ...profile,
            profileImage: imageUrl,
          });
        }

        if (
          recommendationsRes?.status === "success" ||
          recommendationsRes?.data
        ) {
          setRecommendationsData(recommendationsRes.data);
        }
      } catch (err) {
        setError(err.message || "เกิดข้อผิดพลาดในการโหลดข้อมูล");
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, []);

  const quickRecommendations = [
    {
      id: "food",
      title: "การรับประทานอาหาร",
      icon: (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="64"
          height="64"
          viewBox="0 0 64 64"
          fill="none"
        >
          <path
            d="M32 13C28.2422 13 24.5687 14.1143 21.4442 16.2021C18.3196 18.2898 15.8844 21.2572 14.4463 24.729C13.0082 28.2008 12.632 32.0211 13.3651 35.7067C14.0982 39.3924 15.9078 42.7778 18.565 45.435C21.2222 48.0922 24.6077 49.9018 28.2933 50.6349C31.9789 51.368 35.7992 50.9918 39.271 49.5537C42.7428 48.1156 45.7102 45.6804 47.7979 42.5558C49.8857 39.4313 51 35.7578 51 32C50.9944 26.9626 48.9909 22.1331 45.4289 18.5711C41.8669 15.0091 37.0374 13.0056 32 13ZM32 49C28.6377 49 25.351 48.003 22.5553 46.135C19.7597 44.267 17.5807 41.612 16.2941 38.5056C15.0074 35.3993 14.6707 31.9811 15.3267 28.6835C15.9826 25.3858 17.6017 22.3567 19.9792 19.9792C22.3567 17.6017 25.3858 15.9826 28.6835 15.3267C31.9811 14.6707 35.3993 15.0074 38.5056 16.294C41.612 17.5807 44.267 19.7597 46.135 22.5553C48.003 25.3509 49 28.6377 49 32C48.995 36.5071 47.2023 40.8282 44.0153 44.0153C40.8282 47.2023 36.5071 48.995 32 49Z"
            fill="white"
          />
          <path
            d="M13 21V11H11V20H10V11H8V20H7V11H5V20H4V11H2V26C2.00076 26.6203 2.19338 27.2251 2.55144 27.7316C2.90951 28.2381 3.41549 28.6214 4 28.829V52C4 52.7957 4.31607 53.5587 4.87868 54.1213C5.44129 54.6839 6.20435 55 7 55H8C8.79565 55 9.55871 54.6839 10.1213 54.1213C10.6839 53.5587 11 52.7957 11 52V28.829C11.5845 28.6214 12.0905 28.2381 12.4486 27.7316C12.8066 27.2251 12.9992 26.6203 13 26V21ZM11 26C11 26.2652 10.8946 26.5196 10.7071 26.7071C10.5196 26.8946 10.2652 27 10 27C9.73478 27 9.48043 27.1054 9.29289 27.2929C9.10536 27.4804 9 27.7348 9 28V52C9 52.2652 8.89464 52.5196 8.70711 52.7071C8.51957 52.8946 8.26522 53 8 53H7C6.73478 53 6.48043 52.8946 6.29289 52.7071C6.10536 52.5196 6 52.2652 6 52V28C6 27.7348 5.89464 27.4804 5.70711 27.2929C5.51957 27.1054 5.26522 27 5 27C4.73478 27 4.48043 26.8946 4.29289 26.7071C4.10536 26.5196 4 26.2652 4 26V22H11V26ZM61 10C58.6139 10.0026 56.3262 10.9517 54.639 12.639C52.9517 14.3262 52.0026 16.6139 52 19V32C52 32.7956 52.3161 33.5587 52.8787 34.1213C53.4413 34.6839 54.2043 35 55 35V54C55 54.2652 55.1054 54.5196 55.2929 54.7071C55.4804 54.8946 55.7348 55 56 55H61C61.2652 55 61.5196 54.8946 61.7071 54.7071C61.8946 54.5196 62 54.2652 62 54V11C62 10.7348 61.8946 10.4804 61.7071 10.2929C61.5196 10.1054 61.2652 10 61 10ZM60 53H57V34C57 33.7348 56.8946 33.4804 56.7071 33.2929C56.5196 33.1054 56.2652 33 56 33H55C54.7348 33 54.4804 32.8946 54.2929 32.7071C54.1054 32.5196 54 32.2652 54 32V19C54.0021 17.3172 54.6094 15.6914 55.7109 14.4192C56.8125 13.1471 58.3348 12.3137 60 12.071V53Z"
            fill="white"
          />
          <path d="M57 16H55V31H57V16Z" fill="white" />
          <path
            d="M32 22C30.0222 22 28.0888 22.5865 26.4443 23.6853C24.7998 24.7841 23.5181 26.3459 22.7612 28.1732C22.0043 30.0004 21.8063 32.0111 22.1922 33.9509C22.578 35.8907 23.5304 37.6725 24.9289 39.0711C26.3275 40.4696 28.1093 41.422 30.0491 41.8079C31.9889 42.1937 33.9996 41.9957 35.8268 41.2388C37.6541 40.4819 39.2159 39.2002 40.3147 37.5557C41.4135 35.9112 42 33.9778 42 32C41.9971 29.3487 40.9426 26.8069 39.0679 24.9321C37.1931 23.0574 34.6513 22.0029 32 22ZM32 40C30.4178 40 28.871 39.5308 27.5554 38.6518C26.2399 37.7727 25.2145 36.5233 24.609 35.0615C24.0035 33.5997 23.845 31.9911 24.1537 30.4393C24.4624 28.8874 25.2243 27.462 26.3432 26.3431C27.462 25.2243 28.8874 24.4624 30.4393 24.1537C31.9911 23.845 33.5997 24.0035 35.0615 24.609C36.5233 25.2145 37.7727 26.2398 38.6518 27.5554C39.5308 28.871 40 30.4177 40 32C39.9976 34.121 39.154 36.1544 37.6542 37.6542C36.1545 39.154 34.121 39.9976 32 40Z"
            fill="white"
          />
        </svg>
      ),
    },
    {
      id: "exercise",
      title: "การออกกำลังกาย",
      icon: (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="58"
          height="42"
          viewBox="0 0 58 42"
          fill="none"
        >
          <path
            d="M55 6H52V3C52 1.35 50.65 0 49 0H46C44.35 0 43 1.35 43 3V12H36V11C36 10.19 35.68 9.43 35.13 8.89C34.57 8.32 33.81 8 33 8C32.46 8 31.97 8.15 31.53 8.4C31.41 8.22 31.28 8.04 31.12 7.89C30.57 7.32 29.81 7 29 7C28.23 7 27.53 7.3 27 7.78C26.45 7.28 25.76 7 25 7C23.93 7 23 7.56 22.47 8.4C22.03 8.15 21.53 8 21 8C19.35 8 18 9.35 18 11V12H15V3C15 1.35 13.65 0 12 0H9C7.35 0 6 1.35 6 3V6H3C1.35 6 0 7.35 0 9V25C0 26.65 1.35 28 3 28H6V31C6 32.65 7.35 34 9 34H12C13.65 34 15 32.65 15 31V24H18V27C18 27.22 18.07 27.43 18.2 27.6L21 31.33V39C21 40.65 22.35 42 24 42H32C33.65 42 35 40.65 35 39V31.33L37.8 27.6C37.93 27.43 38 27.22 38 27V24H43V31C43 32.65 44.35 34 46 34H49C50.65 34 52 32.65 52 31V28H55C56.65 28 58 26.65 58 25V9C58 7.35 56.65 6 55 6ZM6 26H3C2.45 26 2 25.55 2 25V9C2 8.45 2.45 8 3 8H6V26ZM13 13V23V31C13 31.55 12.55 32 12 32H9C8.45 32 8 31.55 8 31V27V7V3C8 2.45 8.45 2 9 2H12C12.55 2 13 2.45 13 3V13ZM32 11C32 10.45 32.45 10 33 10C33.27 10 33.51 10.1 33.71 10.31C33.9 10.49 34 10.73 34 11V15C34 15.55 33.55 16 33 16C32.45 16 32 15.55 32 15V11ZM28 10C28 9.45 28.45 9 29 9C29.61 9.06 29.94 9.4 30 10V15C30 15.55 29.55 16 29 16C28.45 16 28 15.55 28 15V10ZM24 11V10C24 9.45 24.45 9 25 9C25.27 9 25.51 9.1 25.71 9.31C25.9 9.49 26 9.73 26 10V15C26 15.55 25.55 16 25 16C24.45 16 24 15.55 24 15V11ZM20 11C20 10.45 20.45 10 21 10C21.27 10 21.51 10.1 21.71 10.31C21.9 10.49 22 10.73 22 11V15C22 15.55 21.55 16 21 16C20.45 16 20 15.55 20 15V11ZM36 26.67L33.2 30.4C33.07 30.57 33 30.78 33 31V39C33 39.55 32.55 40 32 40H24C23.45 40 23 39.55 23 39V31C23 30.78 22.93 30.57 22.8 30.4L20 26.67V24H28.18C28.59 25.16 29.69 26 31 26H33C33.55 26 34 25.55 34 25C34 24.45 33.55 24 33 24H31C30.45 24 30 23.55 30 23C30 22.73 30.1 22.49 30.31 22.29C30.49 22.1 30.73 22 31 22H35C35.55 22 36 22.45 36 23V26.67ZM43 22H37.82C37.41 20.84 36.31 20 35 20H31C30.19 20 29.43 20.32 28.89 20.87C28.56 21.19 28.33 21.58 28.18 22H15V14H18V15C18 16.65 19.35 18 21 18C21.77 18 22.47 17.7 23 17.22C23.53 17.7 24.23 18 25 18C25.77 18 26.47 17.7 27 17.22C27.53 17.7 28.23 18 29 18C29.77 18 30.47 17.7 31 17.22C31.53 17.7 32.23 18 33 18C34.65 18 36 16.65 36 15V14H43V22ZM50 7V27V31C50 31.55 49.55 32 49 32H46C45.45 32 45 31.55 45 31V23V13V3C45 2.45 45.45 2 46 2H49C49.55 2 50 2.45 50 3V7ZM56 25C56 25.55 55.55 26 55 26H52V8H55C55.55 8 56 8.45 56 9V25Z"
            fill="#00BBD3"
          />
        </svg>
      ),
    },
    {
      id: "sleep",
      title: "การพักผ่อน",
      icon: (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="48"
          height="28"
          viewBox="0 0 48 28"
          fill="none"
        >
          <path
            d="M47 11H43.321C42.195 8.638 39.786 7 37 7H18C16.393 7 14.916 7.55 13.733 8.463C13.08 6.461 11.217 5 9 5C7.875 5 6.836 5.374 6 6.003V1C6 0.447 5.552 0 5 0H1C0.448 0 0 0.447 0 1V27C0 27.553 0.448 28 1 28H5C5.552 28 6 27.553 6 27V21H11V22C11 22.553 11.448 23 12 23H42V27C42 27.553 42.448 28 43 28H47C47.552 28 48 27.553 48 27V12C48 11.447 47.552 11 47 11ZM4 26H2V2H4V26ZM11 19H6V15H11V19ZM11.946 10.503C11.703 10.922 11.5 11.367 11.347 11.835C10.772 12.571 9.928 13 9 13C7.346 13 6 11.654 6 10C6 8.346 7.346 7 9 7C10.654 7 12 8.346 12 10C12 10.162 11.975 10.332 11.946 10.503ZM42 21H13V14C13 11.243 15.243 9 18 9H37C39.757 9 42 11.243 42 14V21ZM46 26H44V13H46V26Z"
            fill="#00BBD3"
          />
        </svg>
      ),
    },
  ];

  const getDetailContent = () => {
    // Use API data if available, otherwise use default mock data
    if (
      recommendationsData?.has_recommendations &&
      recommendationsData?.recommendations
    ) {
      const { recommendations } = recommendationsData;

      // ใช้รูป hardcode เสมอ (backend ยังไม่รองรับการแก้ไขรูป)
      switch (activeTab) {
        case "food":
          return {
            title: "การรับประทานอาหาร",
            text: recommendations.nutrition_advice || "ยังไม่มีคำแนะนำ",
            image: "./food.jpg",
          };
        case "exercise":
          return {
            title: "การออกกำลังกาย",
            text: recommendations.exercise_advice || "ยังไม่มีคำแนะนำ",
            image: "./exercise.jpg",
          };
        case "sleep":
          return {
            title: "การพักผ่อน",
            text: recommendations.sleep_advice || "ยังไม่มีคำแนะนำ",
            image: "./sleep.jpg",
          };
        default:
          return {
            title: "การรับประทานอาหาร",
            text: recommendations.nutrition_advice || "ยังไม่มีคำแนะนำ",
            image: "./food.jpg",
          };
      }
    }

    // Default mock data
    switch (activeTab) {
      case "food":
        return {
          title:
            "ข้อปฏิบัติการกินอาหาร 9 ข้อ จะช่วยได้ถ้าท่านถือเป็นหลักปฏิบัติตาม",
          text: "ร่างกายเราต้องการสารอาหารที่มีอยู่ในอาหารต่างๆ เพื่อให้มีสุขภาพดีแต่เราจะต้องรู้ว่าจะกินอย่างไร กินอาหารอะไรบ้าง มากน้อยเพียงใดจึงจะได้สารอาหารครบและเพียงพอกับความต้องการของร่างกาย",
          image: "./food.jpg",
        };
      case "exercise":
        return {
          title:
            "ข้อปฏิบัติการออกกำลังกาย 9 ข้อ จะช่วยได้ถ้าท่านถือเป็นหลักปฏิบัติตาม",
          text: "ร่างกายเราต้องการการเคลื่อนไหวในอาการต่างๆ เพื่อให้สุขภาพดีแต่เราจะต้องรู้ว่าจะออกกำลังกายอย่างไร ออกกำลังกายอะไรบ้าง มากน้อยเพียงใดจึงจะได้สารอาหารครบและเพียงพอกับความต้องการของร่างกาย",
          image: "./exercise.jpg",
        };
      case "sleep":
        return {
          title:
            "ข้อปฏิบัติการพักผ่อน 9 ข้อ จะช่วยได้ถ้าท่านถือเป็นหลักปฏิบัติตาม",
          text: "ร่างกายเราต้องการการพักผ่อนในอาการต่างๆ เพื่อให้สุขภาพดีแต่เราจะต้องรู้ว่าจะพักผ่อนอย่างไร พักผ่อนอะไรบ้าง มากน้อยเพียงใดจึงจะได้สารอาหารครบและเพียงพอกับความต้องการของร่างกาย",
          image: "./sleep.jpg",
        };
      default:
        return {
          title:
            "ข้อปฏิบัติการกินอาหาร 9 ข้อ จะช่วยได้ถ้าท่านถือเป็นหลักปฏิบัติตาม",
          text: "ร่างกายเราต้องการสารอาหารที่มีอยู่ในอาหารต่างๆ เพื่อให้มีสุขภาพดีแต่เราจะต้องรู้ว่าจะกินอย่างไร กินอาหารอะไรบ้าง มากน้อยเพียงใดจึงจะได้สารอาหารครบและเพียงพอกับความต้องการของร่างกาย",
          image: "./food.jpg",
        };
    }
  };

  const detailContent = getDetailContent();

  // Debug: Log image path

  // Get profile name and image
  const profileName =
    recommendationsData?.patient_info?.name ||
    profileData?.name ||
    profileData?.firstname ||
    "ผู้ใช้";
  const profileImage = profileData?.profileImage || "./person.png";

  // Get doctor advice
  const doctorAdvice =
    recommendationsData?.recommendations?.doctor_advice ||
    "สิ่งสำคัญคือ พบแพทย์ตามนัดอย่างสม่ำเสมอ และ ทานยาตามที่แพทย์สั่งอย่างเคร่งครัด ห้ามหยุดยาเองเด็ดขาด นอกจากนี้ควรงดเครื่องดื่มแอลกอฮอล์และการสูบบุหรี่เพื่อลดความเสี่ยงภาวะแทรกซ้อน และควรเรียนรู้เกี่ยวกับโรค เพื่อดูแลตัวเองได้อย่างถูกต้องและมีประสิทธิภาพ";

  // Get template matches
  const templateMatches = recommendationsData?.template_matches || [];

  // Show loading state
  if (loading) {
    return (
      <RecommendationsContainer>
        <MainHeader />
        <PageHeader>คำแนะนำสำหรับคุณ</PageHeader>
        <LoadingContainer>กำลังโหลดข้อมูล...</LoadingContainer>
      </RecommendationsContainer>
    );
  }

  return (
    <RecommendationsContainer>
      <MainHeader />

      <PageHeader>คำแนะนำสำหรับคุณ</PageHeader>

      <Content>
        {error && <ErrorMessage>เกิดข้อผิดพลาด: {error}</ErrorMessage>}

        {!recommendationsData?.has_recommendations && !error && (
          <EmptyMessage>ยังไม่มีคำแนะนำสำหรับคุณในขณะนี้</EmptyMessage>
        )}

        {recommendationsData?.has_recommendations && (
          <>
            <MainSection>
              <MainContent>
                <IllustrationContainer></IllustrationContainer>
                <MainText>
                  <MainTitle>
                    <HealthIcon>
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="23"
                        height="23"
                        viewBox="0 0 23 23"
                        fill="none"
                      >
                        <g clipPath="url(#clip0_1356_3304)">
                          <path
                            d="M5.81652 3.92212H4.8693C4.36686 3.92212 3.885 4.12171 3.52972 4.47699C3.17445 4.83227 2.97485 5.31413 2.97485 5.81656V9.13184C2.97485 10.5135 3.52373 11.8387 4.50075 12.8157C5.47776 13.7927 6.80287 14.3416 8.18458 14.3416C9.56628 14.3416 10.8914 13.7927 11.8684 12.8157C12.8454 11.8387 13.3943 10.5135 13.3943 9.13184V5.81656C13.3943 5.31413 13.1947 4.83227 12.8394 4.47699C12.4841 4.12171 12.0023 3.92212 11.4999 3.92212H10.5526"
                            stroke="white"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                          <path
                            d="M7.71118 14.3417C7.71118 15.088 7.85819 15.8271 8.1438 16.5166C8.42941 17.2061 8.84805 17.8326 9.37579 18.3604C9.90354 18.8881 10.5301 19.3068 11.2196 19.5924C11.9091 19.878 12.6482 20.025 13.3945 20.025C14.1409 20.025 14.8799 19.878 15.5694 19.5924C16.259 19.3068 16.8855 18.8881 17.4132 18.3604C17.941 17.8326 18.3596 17.2061 18.6452 16.5166C18.9308 15.8271 19.0778 15.088 19.0778 14.3417V11.5"
                            stroke="white"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                          <path
                            d="M10.5527 2.97485V4.8693"
                            stroke="white"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                          <path
                            d="M5.81665 2.97485V4.8693"
                            stroke="white"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                          <path
                            d="M19.0778 11.5001C20.1241 11.5001 20.9722 10.6519 20.9722 9.60563C20.9722 8.55935 20.1241 7.71118 19.0778 7.71118C18.0315 7.71118 17.1833 8.55935 17.1833 9.60563C17.1833 10.6519 18.0315 11.5001 19.0778 11.5001Z"
                            stroke="white"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </g>
                        <defs>
                          <clipPath id="clip0_1356_3304">
                            <rect
                              width="22.7333"
                              height="22.7333"
                              fill="white"
                              transform="translate(0.133301 0.133301)"
                            />
                          </clipPath>
                        </defs>
                      </svg>
                    </HealthIcon>
                    ผลสุขภาพของคุณ
                  </MainTitle>
                  <MainDescription>
                    {templateMatches.length > 0
                      ? "แปลผลสุขภาพดังนี้"
                      : "ผลตรวจสุขภาพของคุณ"}
                  </MainDescription>
                  <ActionButtons>
                    {templateMatches.length > 0 ? (
                      templateMatches.map((template, index) => (
                        <ActionButton key={index}>
                          {template.template_name}
                        </ActionButton>
                      ))
                    ) : (
                      <ActionButton>ยังไม่มีข้อมูล Template</ActionButton>
                    )}
                  </ActionButtons>
                </MainText>
              </MainContent>
            </MainSection>

            <SectionTitle>คำแนะนำสุขภาพโดยแพทย์</SectionTitle>
            <MainDescription style={{ marginBottom: "20px" }}>
              {renderAdviceText(doctorAdvice)}
            </MainDescription>

            <SectionTitle>คำแนะนำด้านต่างๆ</SectionTitle>

            <RecommendationGrid>
              {quickRecommendations.map((rec) => (
                <RecommendationCard
                  key={rec.id}
                  active={activeTab === rec.id}
                  onClick={() => setActiveTab(rec.id)}
                >
                  <CardIcon active={activeTab === rec.id}>{rec.icon}</CardIcon>
                </RecommendationCard>
              ))}
            </RecommendationGrid>

            <DetailSection>
              <SectionTitle>{detailContent.title}</SectionTitle>
              <DetailCard>
                <DetailImage image={detailContent.image} />
                <DetailContent>
                  <DetailText>
                    {renderAdviceText(detailContent.text)}
                  </DetailText>
                </DetailContent>
              </DetailCard>
            </DetailSection>
          </>
        )}
      </Content>
    </RecommendationsContainer>
  );
};

export default RecommendationsPage;
